import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getGrowthUploadUrl, processGrowthUpload, syncGrowthFromPipedrive, getGrowthDashboard } from '../../services/api';

const ACCEPTED_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
};

const UPLOAD_STEPS = [
  { key: 'select', label: 'Select File' },
  { key: 'uploading', label: 'Uploading' },
  { key: 'processing', label: 'Processing' },
  { key: 'complete', label: 'Complete' },
];

const SYNC_STEPS = [
  { key: 'ready', label: 'Connect' },
  { key: 'fetching', label: 'Fetching Deals' },
  { key: 'computing', label: 'Computing' },
  { key: 'complete', label: 'Complete' },
];

function GrowthImport({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('sync');

  // --- Shared state ---
  const [fiscalYear, setFiscalYear] = useState('2026');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // --- Sync tab state ---
  const [syncStep, setSyncStep] = useState('ready');

  // --- Upload tab state ---
  const [uploadStep, setUploadStep] = useState('select');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch last sync info on mount
  useEffect(() => {
    async function fetchLastSync() {
      try {
        const data = await getGrowthDashboard();
        if (data && data.updated_at) {
          setLastSync({
            time: data.updated_at,
            by: data.updated_by || 'unknown',
            source: data.sync_source || 'upload',
          });
        }
      } catch {
        // Ignore — no existing dashboard yet
      }
    }
    fetchLastSync();
  }, []);

  // --- Pipedrive Sync ---
  async function handleSync() {
    setError('');
    setResult(null);

    try {
      setSyncStep('fetching');

      const syncResult = await syncGrowthFromPipedrive(fiscalYear);

      setSyncStep('complete');
      setResult(syncResult);
      setLastSync({
        time: new Date().toISOString(),
        by: 'you',
        source: 'pipedrive_api',
      });
    } catch (err) {
      setError(err.message || 'Sync failed');
      setSyncStep('ready');
    }
  }

  function handleSyncReset() {
    setSyncStep('ready');
    setError('');
    setResult(null);
  }

  // --- Excel Upload ---
  const validateFile = useCallback((f) => {
    if (!f) return 'No file selected';
    const ext = f.name.toLowerCase().split('.').pop();
    const validExts = ['xlsx', 'xls'];
    if (!ACCEPTED_TYPES[f.type] && !validExts.includes(ext)) {
      return 'Invalid file type. Only Excel files (.xlsx, .xls) are accepted.';
    }
    if (f.size > 10 * 1024 * 1024) {
      return 'File too large. Maximum size is 10MB.';
    }
    return null;
  }, []);

  const handleFile = useCallback((f) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setFile(f);
  }, [validateFile]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  async function handleUpload() {
    if (!file) return;
    setError('');

    try {
      setUploadStep('uploading');
      let uploadUrl, s3Key;
      try {
        ({ uploadUrl, s3Key } = await getGrowthUploadUrl(file.name, file.type));
      } catch (e) {
        throw new Error(`Step 1 (get upload URL): ${e.message}`);
      }

      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`S3 returned ${response.status}: ${text.slice(0, 200)}`);
        }
      } catch (e) {
        if (e.message.startsWith('S3 returned')) throw e;
        throw new Error(`Step 2 (S3 upload): ${e.message}`);
      }

      setUploadStep('processing');
      try {
        const processResult = await processGrowthUpload({ s3Key, fiscalYear });
        setResult(processResult);
        setUploadStep('complete');
        setLastSync({
          time: new Date().toISOString(),
          by: 'you',
          source: 'upload',
        });
      } catch (e) {
        throw new Error(`Step 3 (process upload): ${e.message}`);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      setUploadStep('select');
    }
  }

  function handleUploadReset() {
    setUploadStep('select');
    setFile(null);
    setError('');
    setResult(null);
  }

  // Reset state when switching tabs
  function switchTab(tab) {
    setActiveTab(tab);
    setError('');
    setResult(null);
    if (tab === 'sync') {
      setSyncStep('ready');
    } else {
      setUploadStep('select');
      setFile(null);
    }
  }

  // Step indicators
  const steps = activeTab === 'sync' ? SYNC_STEPS : UPLOAD_STEPS;
  const currentStep = activeTab === 'sync' ? syncStep : uploadStep;
  const currentStepIdx = steps.findIndex((s) => s.key === currentStep);

  function formatLastSync(info) {
    if (!info) return null;
    try {
      const d = new Date(info.time);
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const source = info.source === 'pipedrive_api' ? 'Pipedrive Sync' : 'Excel Upload';
      return `${dateStr} at ${timeStr} via ${source}`;
    } catch {
      return null;
    }
  }

  // --- Result Summary (shared between both tabs) ---
  function renderResultSummary() {
    if (!result) return null;
    return (
      <div style={{
        marginTop: '16px',
        padding: '16px',
        background: 'var(--bcore-hover-subtle)',
        borderRadius: '8px',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '12px',
        color: 'var(--bcore-text-muted)',
        textAlign: 'left',
        maxWidth: '360px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}>
        <div style={{ marginBottom: '6px' }}>
          <span style={{ color: 'var(--bcore-text-dim)' }}>Total Deals:</span>{' '}
          <span style={{ color: 'var(--bcore-accent)' }}>{result.total_deals}</span>
        </div>
        <div style={{ marginBottom: '6px' }}>
          <span style={{ color: 'var(--bcore-text-dim)' }}>Qualified Pipeline:</span>{' '}
          <span style={{ color: 'var(--bcore-accent)' }}>{result.qualified_count}</span>
        </div>
        <div style={{ marginBottom: '6px' }}>
          <span style={{ color: 'var(--bcore-text-dim)' }}>Submitted:</span>{' '}
          <span style={{ color: 'var(--bcore-accent)' }}>{result.submitted_count}</span>
        </div>
        <div>
          <span style={{ color: 'var(--bcore-text-dim)' }}>Won:</span>{' '}
          <span style={{ color: 'var(--bcore-accent)' }}>{result.won_count}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-card">
        <h2 style={{
          fontFamily: 'Kode Mono, monospace',
          fontSize: '18px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '16px',
          color: 'var(--bcore-text)',
        }}>
          Growth Import
        </h2>

        {/* Last Sync Info */}
        {lastSync && (
          <div style={{
            marginBottom: '16px',
            padding: '8px 12px',
            background: 'var(--bcore-hover-subtle)',
            borderRadius: '8px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '11px',
            color: 'var(--bcore-text-dim)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Last updated: {formatLastSync(lastSync)}
          </div>
        )}

        {/* Tab Bar */}
        <div className="tab-bar" style={{ marginBottom: '20px' }}>
          <button
            className={`tab-btn${activeTab === 'sync' ? ' active' : ''}`}
            onClick={() => switchTab('sync')}
          >
            Pipedrive Sync
          </button>
          <button
            className={`tab-btn${activeTab === 'upload' ? ' active' : ''}`}
            onClick={() => switchTab('upload')}
          >
            Upload Excel
          </button>
        </div>

        {/* Progress Steps */}
        <div className="upload-steps">
          {steps.map((s, idx) => (
            <div
              key={s.key}
              className={`upload-step${idx <= currentStepIdx ? ' active' : ''}${idx < currentStepIdx ? ' completed' : ''}`}
            >
              <div className="upload-step-dot">
                {idx < currentStepIdx ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span className="upload-step-label">{s.label}</span>
              {idx < steps.length - 1 && <div className="upload-step-line" />}
            </div>
          ))}
        </div>

        {/* ========= SYNC TAB ========= */}
        {activeTab === 'sync' && (
          <>
            {/* Ready State */}
            {syncStep === 'ready' && (
              <>
                <div className="form-section" style={{ marginBottom: '20px' }}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Fiscal Year <span className="required">*</span></label>
                      <select
                        className="form-input"
                        value={fiscalYear}
                        onChange={(e) => setFiscalYear(e.target.value)}
                      >
                        <option value="2025">FY2025</option>
                        <option value="2026">FY2026</option>
                        <option value="2027">FY2027</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Sync Info Panel */}
                <div style={{
                  padding: '20px',
                  background: 'var(--bcore-hover-subtle)',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  textAlign: 'center',
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--bcore-accent)" strokeWidth="1.5" style={{ marginBottom: '12px' }}>
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '13px',
                    color: 'var(--bcore-text)',
                    marginBottom: '6px',
                    fontWeight: 600,
                  }}>
                    Sync directly from Pipedrive
                  </div>
                  <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '11px',
                    color: 'var(--bcore-text-dim)',
                    lineHeight: '1.5',
                  }}>
                    Fetches all deals via the Pipedrive API, maps custom fields,
                    and rebuilds the growth dashboard snapshot automatically.
                  </div>
                </div>

                {error && (
                  <div className="form-api-error">{error}</div>
                )}

                <div className="form-actions">
                  <button className="btn btn-primary" onClick={handleSync}>
                    Sync from Pipedrive
                  </button>
                </div>
              </>
            )}

            {/* Fetching State */}
            {syncStep === 'fetching' && (
              <div className="upload-processing">
                <div className="upload-spinner" />
                <div className="upload-processing-title">Syncing from Pipedrive</div>
                <div className="upload-processing-sub">
                  Fetching deals and computing dashboard metrics...
                </div>
              </div>
            )}

            {/* Complete State */}
            {syncStep === 'complete' && (
              <div className="upload-processing">
                <div className="upload-complete-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="upload-processing-title">Sync Complete</div>
                <div className="upload-processing-sub">
                  Growth dashboard has been updated from Pipedrive.
                </div>
                {renderResultSummary()}
                <div className="form-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
                  <button className="btn btn-outline" onClick={handleSyncReset}>
                    Sync Again
                  </button>
                  <button className="btn btn-primary" onClick={() => onNavigate('growth-dashboard')}>
                    View Dashboard
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ========= UPLOAD TAB ========= */}
        {activeTab === 'upload' && (
          <>
            {/* Select File State */}
            {uploadStep === 'select' && (
              <>
                <div className="form-section" style={{ marginBottom: '20px' }}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Fiscal Year <span className="required">*</span></label>
                      <select
                        className="form-input"
                        value={fiscalYear}
                        onChange={(e) => setFiscalYear(e.target.value)}
                      >
                        <option value="2025">FY2025</option>
                        <option value="2026">FY2026</option>
                        <option value="2027">FY2027</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Dropzone */}
                <div
                  className={`upload-dropzone${dragActive ? ' drag-active' : ''}${file ? ' has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={handleInputChange}
                    style={{ display: 'none' }}
                  />
                  {file ? (
                    <div className="upload-file-info">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <line x1="10" y1="9" x2="8" y2="9" />
                      </svg>
                      <div className="upload-file-name">{file.name}</div>
                      <div className="upload-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                      <button
                        className="upload-file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="upload-dropzone-content">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <div className="upload-dropzone-text">
                        Drag and drop your Pipedrive export here
                      </div>
                      <div className="upload-dropzone-hint">
                        or click to browse &bull; .xlsx, .xls &bull; Max 10MB
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="form-api-error">{error}</div>
                )}

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={!file}
                  >
                    Upload & Process
                  </button>
                </div>
              </>
            )}

            {/* Uploading State */}
            {uploadStep === 'uploading' && (
              <div className="upload-processing">
                <div className="upload-spinner" />
                <div className="upload-processing-title">Uploading Spreadsheet</div>
                <div className="upload-processing-sub">
                  Transferring {file?.name} to secure storage...
                </div>
              </div>
            )}

            {/* Processing State */}
            {uploadStep === 'processing' && (
              <div className="upload-processing">
                <div className="upload-spinner" />
                <div className="upload-processing-title">Processing Pipedrive Data</div>
                <div className="upload-processing-sub">
                  Parsing deals and computing dashboard metrics...
                </div>
              </div>
            )}

            {/* Complete State */}
            {uploadStep === 'complete' && (
              <div className="upload-processing">
                <div className="upload-complete-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="upload-processing-title">Import Complete</div>
                <div className="upload-processing-sub">
                  Growth dashboard has been updated successfully.
                </div>
                {renderResultSummary()}
                <div className="form-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
                  <button className="btn btn-outline" onClick={handleUploadReset}>
                    Upload Another
                  </button>
                  <button className="btn btn-primary" onClick={() => onNavigate('growth-dashboard')}>
                    View Dashboard
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

GrowthImport.propTypes = {
  onNavigate: PropTypes.func.isRequired,
};

export default GrowthImport;
